import moment from 'moment';
import { OUTCOME_STATUS } from 'domain/shared/newProject';

export type OutcomeActiveForInput = {
  status?: string;
  createdAt?: Nullable<string>;
  // Completed outcomes are immutable in the UI, so their last write (updatedAt) is the moment
  // they were completed — the fixed end of the "Active for" period.
  updatedAt?: Nullable<string>;
};

const DURATION_UNITS: { unit: moment.unitOfTime.Diff; label: string }[] = [
  { unit: 'years', label: 'year' },
  { unit: 'months', label: 'month' },
  { unit: 'days', label: 'day' },
];

// Whole-day span between two dates as "12 days" / "4 months" / "1 year 2 months".
const formatDuration = (from: moment.MomentInput, to: moment.MomentInput): string | null => {
  const start = moment(from).startOf('day');
  const end = moment(to).startOf('day');

  if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
    return null;
  }

  const parts = DURATION_UNITS.reduce<string[]>((acc, { unit, label }) => {
    const value = end.diff(start, unit);
    if (value > 0) {
      acc.push(`${value} ${label}${value === 1 ? '' : 's'}`);
      start.add(value, unit);
    }
    return acc;
  }, []);

  return parts.length ? parts.join(' ') : '0 days';
};

export const getOutcomeActiveDuration = ({ status, createdAt, updatedAt }: OutcomeActiveForInput): string | null => {
  if (!createdAt) {
    return null;
  }

  const end = status === OUTCOME_STATUS.COMPLETED ? updatedAt : moment();
  if (!end) {
    return null;
  }

  return formatDuration(createdAt, end);
};

export const getOutcomeActiveForText = (outcome: OutcomeActiveForInput): string | null => {
  const duration = getOutcomeActiveDuration(outcome);
  return duration ? `Active for ${duration}` : null;
};
